import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import thaiAddressesJson from "../../../thai-addresses.min.json";

import {
  CustomerAddress,
  CustomerAddressCreateRequest,
  UpdateCustomerAddressRequest,
} from "../../types/crm";

const addressData = (thaiAddressesJson as { data: Record<string, Record<string, Record<string, number[]>>> }).data;
const provinceOptions = Object.keys(addressData).sort();

const addressSchema = z.object({
  ecp_code: z.string().optional(),
  shop_name: z.string().min(1, "Store Name is required"),
  phone: z.string().min(1, "Phone Number is required"),
  address: z.string().min(1, "Address Details is required"),
  province: z.string().min(1, "Province is required"),
  district: z.string().min(1, "District is required"),
  sub_district: z.string().min(1, "Subdistrict is required"),
  zip_code: z.string().min(1, "Postal Code is required"),
});

const createAddressSchema = addressSchema.extend({
  ecp_code: z.string().min(1, "ECP Code is required"),
});

type FormValues = z.infer<typeof addressSchema>;

type PropsType = {
  address?: CustomerAddress | null;
  ecpCode?: string;
  onSubmit: (
    data: CustomerAddressCreateRequest | UpdateCustomerAddressRequest,
    ecpCode?: string,
  ) => Promise<void>;
  onCancel: () => void;
};

const labelSx = {
  fontSize: 16,
  lineHeight: 1.4,
  color: "#25272C",
};

const AddressForm = ({ address, ecpCode, onSubmit, onCancel }: PropsType) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!address;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(isEditing ? addressSchema : createAddressSchema),
    defaultValues: {
      ecp_code: address?.ecp_code || ecpCode || "",
      shop_name: address?.shop_name || "",
      phone: address?.phone || "",
      address: address?.address || "",
      province: address?.province || "",
      district: address?.district || "",
      sub_district: address?.sub_district || "",
      zip_code: address?.zip_code || "",
    },
  });

  const watchedProvince = watch("province");
  const watchedDistrict = watch("district");

  const districtOptions = watchedProvince
    ? Object.keys(addressData[watchedProvince] ?? {}).sort()
    : [];

  const subDistrictOptions =
    watchedProvince && watchedDistrict && addressData[watchedProvince]?.[watchedDistrict]
      ? Object.keys(addressData[watchedProvince][watchedDistrict]).sort()
      : [];

  const handleFormSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const { ecp_code, ...formData } = data;
      await onSubmit(formData, ecp_code || ecpCode);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        {/* ECP Code */}
        <Stack spacing={1}>
          <Typography sx={labelSx}>
            ECP Code
            {!isEditing && (
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            )}
          </Typography>
          {isEditing ? (
            <TextField
              value={address?.ecp_code || ecpCode || ""}
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
                sx: { bgcolor: "#F3F4F6" },
              }}
            />
          ) : (
            <Controller
              name="ecp_code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter ECP Code"
                  fullWidth
                  error={!!errors.ecp_code}
                  helperText={errors.ecp_code?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          )}
        </Stack>

        {/* Store Name */}
        <Stack spacing={1}>
          <Typography sx={labelSx}>
            Store Name
            <Typography component="span" sx={{ color: "#BE2217" }}>
              *
            </Typography>
          </Typography>
          <Controller
            name="shop_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Enter Shop Name"
                fullWidth
                error={!!errors.shop_name}
                helperText={errors.shop_name?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>

        {/* Phone Number */}
        <Stack spacing={1}>
          <Typography sx={labelSx}>
            Phone Number
            <Typography component="span" sx={{ color: "#BE2217" }}>
              *
            </Typography>
          </Typography>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Enter Phone Number"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>

        {/* Address Details */}
        <Stack spacing={1}>
          <Typography sx={labelSx}>
            Address Details
            <Typography component="span" sx={{ color: "#BE2217" }}>
              *
            </Typography>
          </Typography>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Enter Address Details"
                fullWidth
                multiline
                minRows={2}
                error={!!errors.address}
                helperText={errors.address?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>

        {/* Province + District */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Province
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Controller
              name="province"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  error={!!errors.province}
                  helperText={errors.province?.message}
                  disabled={isSubmitting}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("district", "");
                    setValue("sub_district", "");
                    setValue("zip_code", "");
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: "#8E95A2", fontSize: 16 }}>
                            Select Province
                          </Typography>
                        );
                      }
                      return value as string;
                    },
                  }}
                >
                  {provinceOptions.map((province) => (
                    <MenuItem key={province} value={province}>
                      {province}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              District
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Controller
              name="district"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  error={!!errors.district}
                  helperText={errors.district?.message}
                  disabled={isSubmitting || !watchedProvince}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("sub_district", "");
                    setValue("zip_code", "");
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: "#8E95A2", fontSize: 16 }}>
                            {watchedProvince ? "Select District" : "Select Province first"}
                          </Typography>
                        );
                      }
                      return value as string;
                    },
                  }}
                >
                  {districtOptions.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </Stack>

        {/* Subdistrict + Postal Code */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Subdistrict
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Controller
              name="sub_district"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  error={!!errors.sub_district}
                  helperText={errors.sub_district?.message}
                  disabled={isSubmitting || !watchedDistrict}
                  onChange={(e) => {
                    const subDistrict = e.target.value as string;
                    field.onChange(e);
                    if (watchedProvince && watchedDistrict && subDistrict) {
                      const codes = addressData[watchedProvince]?.[watchedDistrict]?.[subDistrict];
                      if (codes && codes.length > 0) {
                        setValue("zip_code", String(codes[0]));
                      }
                    }
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: "#8E95A2", fontSize: 16 }}>
                            {watchedDistrict ? "Select Subdistrict" : "Select District first"}
                          </Typography>
                        );
                      }
                      return value as string;
                    },
                  }}
                >
                  {subDistrictOptions.map((sub) => (
                    <MenuItem key={sub} value={sub}>
                      {sub}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Postal Code
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Controller
              name="zip_code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Postal Code"
                  fullWidth
                  error={!!errors.zip_code}
                  helperText={errors.zip_code?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Stack>
        </Stack>

        {/* Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              height: 40,
              px: 3,
              borderRadius: "96px",
              borderColor: "#111",
              color: "#111",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
            }}
          >
            Discard
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{
              height: 40,
              px: 3,
              borderRadius: "96px",
              bgcolor: "#111",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AddressForm;
